// components
import { TabContext } from '@mui/lab';
import { FilterPopper } from './FilterPopper';
import { FlexMatchDrawerFilters } from './FlexMatchDrawerFilters';
import { FlexMatchDrawerSettings } from './FlexMatchDrawerSettings';
import { MatchDrawerTabs } from './MatchDrawerTabs';
import { StyledDrawerTabPanel } from './MatchDrawerTables.styles';
import { EmployeesTable } from './EmployeesTable';

// constants
import {
  DEFAULT_EMPLOYEE_FLEX_FILTER,
  FLEX_DRAWER_FILTER_DEFAULT_VALUES
} from './MatchDrawerTables.consts';

// hooks
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useGetFlexMatchCandidates from '../hooks/useGetFlexMatchCandidates';
import useGetFlexMatchEmployees from '../hooks/useGetFlexMatchEmployees';
import { useGetEmployeesColumns } from '../hooks/useGetEmployeesColumns';
import useSavePreferences from '../hooks/useSavePreferences';

// types
import {
  FilterSteps,
  FilterValues,
  MatchDrawerTablesProps
} from './MatchDrawerTables.types';

// utils
import { yupResolver } from '@hookform/resolvers/yup';
import { flexDrawerFilterSchema } from './MatchDrawerTables.schema';
import {
  extractCheckedFromValues,
  gotoNextStep,
  gotoPrevStep,
  onAddStep,
  onDeleteStep,
  onReorderSteps,
  toFormFromStep
} from './MatchDrawerTables.utils';

export const MatchDrawerTables = ({
  selectedRow,
  handlePreferredEmployees,
  onClose,
  isOpen
}: MatchDrawerTablesProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch'
  });
  const { t: tCommon } = useTranslation('common');
  const [tab, setTab] = useState('pendingRequests');
  const [filters, setFilters] = useState<FilterValues>(
    DEFAULT_EMPLOYEE_FLEX_FILTER
  );
  const [filterSteps, setFilterSteps] = useState<FilterSteps[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(-1);
  const currentStep =
    currentStepIndex >= 0 ? filterSteps[currentStepIndex] : undefined;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const columns = useGetEmployeesColumns();
  const { mutateAsync: savePreference } = useSavePreferences();
  const [editingStepId, setEditingStepId] = useState<string | number | null>(
    null
  );
  const [draftStepId, setDraftStepId] = useState<string | number | null>(null);

  const editingStep = useMemo(
    () =>
      editingStepId != null
        ? filterSteps.find((step) => step.id === editingStepId)
        : undefined,
    [editingStepId, filterSteps]
  );

  const baselineStep = editingStep ?? currentStep;

  const activeFilterLabel =
    editingStepId != null
      ? editingStep?.name?.trim() || t('newStep')
      : currentStepIndex >= 0
      ? currentStep?.name ?? ''
      : t('customFilter');

  const handleTabChange = (event: SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const methods = useForm({
    defaultValues: FLEX_DRAWER_FILTER_DEFAULT_VALUES,
    resolver: yupResolver(flexDrawerFilterSchema)
  });

  const watchedForm = methods.watch();

  const asBooleanValues = useMemo(
    () =>
      (values: Record<string, unknown> = {}) =>
        Object.fromEntries(
          Object.entries(values).map(([key, value]) => [
            key,
            value === true || value === 'true'
          ])
        ) as FilterValues,
    []
  );

  const applyStepFilters = useCallback(
    (step?: FilterSteps) => {
      const formVals = toFormFromStep(step);
      methods.reset(formVals);
      const runtimeFilters = extractCheckedFromValues(formVals);
      setFilters(runtimeFilters as FilterValues);
    },
    [methods]
  );

  const { data: employees, isLoading: isLoadingEmployees } =
    useGetFlexMatchEmployees(
      {
        requestId: selectedRow?.requestId,
        filters
      },
      {
        enabled: !!selectedRow?.requestId
      }
    );

  const {
    data: candidates,
    isLoading: isLoadingCandidates,
    isFetching: isFetchingCandidates,
    isRefetching: isRefetchingCandidates
  } = useGetFlexMatchCandidates(
    {
      requestId: selectedRow?.requestId
    },
    {
      enabled: !!selectedRow?.requestId
    }
  );

  const filteredEmployees = employees?.filter(
    ({ employeeId }) =>
      employeeId !==
      candidates?.find((candidate) => candidate.employeeId === employeeId)
        ?.employeeId
  );

  const handleFilterClick = () => {
    setIsFilterOpen(true);
  };

  const handleClose = () => {
    setIsFilterOpen(false);

    if (draftStepId != null) {
      setFilterSteps((prev) =>
        prev
          .filter((step) => step.id !== draftStepId)
          .map((step, index) => ({ ...step, index }))
      );
      setDraftStepId(null);
    }

    setEditingStepId(null);
  };

  const onClearFilters = () => {
    methods.reset(FLEX_DRAWER_FILTER_DEFAULT_VALUES);
    setFilters(DEFAULT_EMPLOYEE_FLEX_FILTER);
  };

  const onApplyFilters = methods.handleSubmit((values: FilterValues) => {
    const runtimeFilters = extractCheckedFromValues(values);
    setFilters(runtimeFilters as FilterValues);

    const formKey = toComparable(runtimeFilters as Partial<FilterValues>);
    const idx = filterSteps.findIndex((step) => {
      const stepKey = toComparable(asBooleanValues(step.checkedFilters));
      return stepKey === formKey;
    });
    setCurrentStepIndex(idx);

    handleClose();
  });

  const normalizeValues = useCallback((values?: Partial<FilterValues>) => {
    const out: Record<string, any> = {};
    if (!values) return out;

    for (const [key, value] of Object.entries(values)) {
      if (value === true || value === 'true') out[key] = true;
      if (key === 'balanceHours' && value !== '') {
        const balance = Number(value);
        if (Number.isFinite(balance)) out[key] = balance;
      }
    }
    return out;
  }, []);

  const toComparable = useCallback(
    (values?: Partial<FilterValues>) => {
      const normalizedFilterValues = normalizeValues(values);
      const normalizedDefaultFilterValues = normalizeValues(
        FLEX_DRAWER_FILTER_DEFAULT_VALUES
      );

      for (const key of Object.keys(normalizedFilterValues)) {
        if (normalizedFilterValues[key] === normalizedDefaultFilterValues[key])
          delete normalizedFilterValues[key];
      }

      const sorted = Object.keys(normalizedFilterValues)
        .sort()
        .reduce(
          (acc, key) => {
            acc[key] = normalizedFilterValues[key];
            return acc;
          },
          {} as Record<string, any>
        );
      return JSON.stringify(sorted);
    },
    [normalizeValues]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!baselineStep) return false;

    const formName = (watchedForm as any)?.stepName ?? '';
    const nameChanged = formName.trim() !== (baselineStep.name ?? '').trim();

    const formChecked = extractCheckedFromValues(watchedForm || {});
    const formKey = toComparable(formChecked as FilterValues);

    const stepKey = toComparable(baselineStep.checkedFilters as any);
    const filtersChanged = formKey !== stepKey;

    const isDraftBaseline = !!baselineStep && draftStepId === baselineStep.id;
    return isDraftBaseline || nameChanged || filtersChanged;
  }, [watchedForm, baselineStep, toComparable, draftStepId]);

  const persistSteps = (next: FilterSteps[]) => {
    const preferences = JSON.parse(
      sessionStorage.getItem('userPreferences') || '[]'
    );

    const without = preferences.filter(
      (preference) => preference.key !== 'flexMatchSteps'
    );

    sessionStorage.setItem(
      'userPreferences',
      JSON.stringify([
        ...without,
        { key: 'flexMatchSteps', value: JSON.stringify(next) }
      ])
    );

    savePreference([{ key: 'flexMatchSteps', value: JSON.stringify(next) }]);
  };

  const onSaveSelectedStep = methods.handleSubmit((values: any) => {
    const target = editingStep ?? currentStep;
    if (!target) return;

    const nextName = (values.stepName ?? '').trim();
    const nextChecked = extractCheckedFromValues(values);

    setFilterSteps((prev) => {
      const next = prev.map((step) =>
        step.id === target.id
          ? { ...step, name: nextName, checkedFilters: nextChecked }
          : step
      );
      persistSteps(next);

      const idx = next.findIndex((step) => step.id === target.id);
      setCurrentStepIndex(idx);
      return next;
    });

    if (draftStepId === target.id) setDraftStepId(null);

    const formVals = toFormFromStep({
      ...target,
      name: nextName,
      checkedFilters: nextChecked
    });

    methods.reset(
      {
        ...formVals
      },
      { keepDirty: false }
    );
    setFilters(extractCheckedFromValues(formVals) as FilterValues);
  });

  const onStartEditing = (targetStep: FilterSteps) => {
    const idx = filterSteps.findIndex((step) => step.id === targetStep.id);
    if (idx === -1) return;
    setCurrentStepIndex(idx);
    setEditingStepId(targetStep.id!);
    applyStepFilters(targetStep);
  };

  useEffect(() => {
    if (!filterSteps.length || editingStepId != null) return;

    const sub = methods.watch((formValues) => {
      const formKey = toComparable(formValues as FilterValues);
      const idx = filterSteps.findIndex((step) => {
        const stepKey = toComparable(asBooleanValues(step.checkedFilters));
        return stepKey === formKey;
      });
      setCurrentStepIndex(idx);
    });

    return () => sub.unsubscribe?.();
  }, [methods, filterSteps, asBooleanValues, toComparable, editingStepId]);

  useEffect(() => {
    const preferences = JSON.parse(
      sessionStorage.getItem('userPreferences') || '[]'
    );
    const stored = JSON.parse(
      preferences.find(
        (preference: { key: string }) => preference.key === 'flexMatchSteps'
      )?.value || '[]'
    ) as FilterSteps[];

    if (stored.length) {
      setFilterSteps(stored);
      setCurrentStepIndex(0);
      applyStepFilters(stored[0]);
    }
  }, [applyStepFilters]);

  if (!selectedRow) return null;

  return (
    <TabContext value={tab}>
      <MatchDrawerTabs
        filters={filters}
        setFilters={setFilters}
        filterSteps={filterSteps}
        currentStepIndex={currentStepIndex}
        gotoPrevStep={() =>
          gotoPrevStep({ setCurrentStepIndex, applyStepFilters, filterSteps })
        }
        gotoNextStep={() =>
          gotoNextStep({ setCurrentStepIndex, applyStepFilters, filterSteps })
        }
        methods={methods}
        handleFilterClick={handleFilterClick}
        tab={tab}
        candidates={candidates}
        employees={filteredEmployees}
        handleTabChange={handleTabChange}
        handlePreferredEmployees={handlePreferredEmployees}
        selectedRow={selectedRow}
        activeFilterLabel={activeFilterLabel}
        t={t}
        tCommon={tCommon}
      />
      <StyledDrawerTabPanel value="pendingRequests">
        <EmployeesTable
          selectedRow={selectedRow}
          data={candidates ?? []}
          columns={columns}
          isLoading={
            isLoadingCandidates ||
            isFetchingCandidates ||
            isRefetchingCandidates
          }
          rowId={(row) => row.employeeId + row.workAgreementId}
          isCandidatesTable
          onClose={onClose}
        />
      </StyledDrawerTabPanel>
      <StyledDrawerTabPanel value="openToQueryAndLink">
        <EmployeesTable
          selectedRow={selectedRow}
          data={filteredEmployees ?? []}
          columns={columns}
          isLoading={isLoadingEmployees}
          rowId={(row) => row.employeeId + row.workAgreementId}
          onClose={onClose}
          isOpen={isOpen}
        />

        <FormProvider {...methods}>
          <FilterPopper
            filterMainLabel={t('matchSteps')}
            isOpen={isFilterOpen}
            getAnchorEl={() => document.getElementById('filter-drawer-popover')}
            filterId="filter-drawer-popover"
            handleClose={handleClose}
            handleClearFilters={onClearFilters}
            handleApplyFilters={onApplyFilters}
            enableSettings
            isEditMode={!!baselineStep && hasUnsavedChanges}
            onSave={onSaveSelectedStep}
            saveDisabled={!hasUnsavedChanges || !baselineStep}
            settingsLabel={t('stepsList')}
            settingsAction={{
              label: t('add'),
              icon: 'plus',
              onClick: () =>
                onAddStep({
                  draftStepId,
                  setFilterSteps,
                  setDraftStepId,
                  setEditingStepId,
                  setCurrentStepIndex,
                  methods,
                  setFilters
                })
            }}
            SettingsComponent={
              <FlexMatchDrawerSettings
                filterSteps={filterSteps}
                currentStep={currentStep}
                onReorderSteps={(activeId, overId) =>
                  onReorderSteps({
                    activeId,
                    overId,
                    setFilterSteps,
                    currentStepIndex,
                    setCurrentStepIndex,
                    persistSteps
                  })
                }
                onDeleteStep={(id) =>
                  onDeleteStep({
                    id,
                    setFilters,
                    setFilterSteps,
                    currentStepIndex,
                    setCurrentStepIndex,
                    methods,
                    applyStepFilters,
                    persistSteps
                  })
                }
                onStartEditing={onStartEditing}
              />
            }
          >
            <FlexMatchDrawerFilters />
          </FilterPopper>
        </FormProvider>
      </StyledDrawerTabPanel>
    </TabContext>
  );
};
